import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toOptions } from "../../lib/companyIntent";
import {
  LEAD_SOURCE_OTHER,
  type LeadSourceErrors,
  type LeadSourceValue,
  needsLeadSourceDetail,
} from "../../lib/leadSource";
import { useCompaniesLeadSourceOptionsRetrieve } from "../../services/companies/generated/companies/companies";
import "../CompanyIntentFields/CompanyIntentFields.css";

interface LeadSourceFieldsProps {
  value: LeadSourceValue;
  onChange: (next: LeadSourceValue) => void;
  errors?: LeadSourceErrors;
  disabled?: boolean;
  idPrefix: string;
}

/**
 * Source type + named source (managed by super moderators) + "Other — please
 * specify" free text. Registration only (WP1.9).
 */
const LeadSourceFields = ({ value, onChange, errors = {}, disabled = false, idPrefix }: LeadSourceFieldsProps) => {
  const { t } = useTranslation("components");
  const { data, isLoading, isError } = useCompaniesLeadSourceOptionsRetrieve({
    query: { staleTime: 10 * 60 * 1000 },
  });
  const types = useMemo(() => toOptions(data?.types), [data]);
  const sources = useMemo(
    () =>
      (data?.sources ?? []).filter(
        (s): s is { id: number; name: string; type: string } =>
          typeof s.id === "number" && Boolean(s.name) && s.type === value.type,
      ),
    [data, value.type],
  );
  const id = (name: string) => `${idPrefix}-${name}`;
  const showSources = value.type !== "" && value.type !== LEAD_SOURCE_OTHER && sources.length > 0;
  const showDetail = value.type !== "" && (needsLeadSourceDetail(value) || !showSources);

  if (isError) {
    return (
      <p className="intent-fields__error" role="alert">
        {t("leadSource.loadError")}
      </p>
    );
  }

  return (
    <div className="intent-fields" aria-busy={isLoading}>
      <div className="intent-fields__row">
        <div className="intent-fields__field">
          <label className="intent-fields__label" htmlFor={id("type")}>
            {t("leadSource.typeLabel")} <span className="intent-fields__required">*</span>
          </label>
          <select
            id={id("type")}
            className="intent-fields__control"
            value={value.type}
            onChange={(e) => onChange({ type: e.target.value, sourceId: "", detail: value.detail })}
            disabled={disabled}
          >
            <option value="">{t("leadSource.select")}</option>
            {types.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.leadSourceType && <p className="intent-fields__error">{errors.leadSourceType}</p>}
        </div>

        {showSources && (
          <div className="intent-fields__field">
            <label className="intent-fields__label" htmlFor={id("source")}>
              {t("leadSource.sourceLabel")}
            </label>
            <select
              id={id("source")}
              className="intent-fields__control"
              value={value.sourceId}
              onChange={(e) => onChange({ ...value, sourceId: e.target.value })}
              disabled={disabled}
            >
              <option value="">{t("leadSource.otherOption")}</option>
              {sources.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.leadSourceId && <p className="intent-fields__error">{errors.leadSourceId}</p>}
          </div>
        )}
      </div>

      {showDetail && (
        <div className="intent-fields__field">
          <label className="intent-fields__label" htmlFor={id("detail")}>
            {t("leadSource.detailLabel")} <span className="intent-fields__required">*</span>
          </label>
          <input
            id={id("detail")}
            type="text"
            className="intent-fields__control"
            maxLength={255}
            placeholder={t("leadSource.detailPlaceholder")}
            value={value.detail}
            onChange={(e) => onChange({ ...value, detail: e.target.value })}
            disabled={disabled}
          />
          {errors.leadSourceDetail && <p className="intent-fields__error">{errors.leadSourceDetail}</p>}
          {!showSources && errors.leadSourceId && <p className="intent-fields__error">{errors.leadSourceId}</p>}
        </div>
      )}
    </div>
  );
};

export default LeadSourceFields;
